import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { loadConfig, getAuthToken } from '../services/api';

const DateWisePaymentReport = ({ currentUser }) => {
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gridApi, setGridApi] = useState(null);
  const [summaryStats, setSummaryStats] = useState(null);
  const [viewMode, setViewMode] = useState('both'); // 'sheet', 'chart', 'both'

  // Fetch date-wise payment report data
  const fetchDateWisePaymentReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const baseUrl = await loadConfig();
      const token = getAuthToken();
      
      const response = await fetch(`${baseUrl}/Reports/date-wise-payment`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/plain, application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch report: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const transformedData = data.data.map((item, index) => ({
          ...item,
          id: index,
          date: item.date,
          dateFormatted: new Date(item.date).toLocaleDateString('en-IN'),
        }));
        
        // Sort by date descending
        transformedData.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setReportData(transformedData);
        calculateSummaryStats(transformedData);
      } else {
        setReportData([]);
        setSummaryStats(null);
      }
    } catch (err) {
      console.error('Error fetching date-wise payment report:', err);
      setError(err.message || 'Failed to load report');
      setReportData([]);
      setSummaryStats(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate summary statistics
  const calculateSummaryStats = (data) => {
    if (data.length === 0) {
      setSummaryStats(null);
      return;
    }

    const stats = {
      totalDays: data.length,
      totalAmount: data.reduce((sum, item) => sum + (item.totalAmountReceived || 0), 0),
      totalAdvances: data.reduce((sum, item) => sum + (item.totalAdvances || 0), 0),
      averagePerDay: 0,
      averagePerAdvance: 0,
    };

    stats.averagePerDay = stats.totalDays > 0 ? stats.totalAmount / stats.totalDays : 0;
    stats.averagePerAdvance = stats.totalAdvances > 0 ? stats.totalAmount / stats.totalAdvances : 0;
    setSummaryStats(stats);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchDateWisePaymentReport();
  }, []);

  // Currency formatter
  const currencyFormatter = (value) => {
    return `₹${Number(value || 0).toLocaleString('en-IN')}`;
  };

  // Column definitions for ag-grid
  const columnDefs = [
    {
      field: 'dateFormatted',
      headerName: 'Date',
      width: 120,
      filter: 'agDateColumnFilter',
      sort: 'desc',
      cellClass: 'text-sm font-medium text-gray-700',
      pinned: 'left',
    },
    {
      field: 'totalAdvances',
      headerName: 'Total Advances',
      width: 150,
      filter: 'agNumberColumnFilter',
      cellClass: 'text-sm text-right font-medium text-blue-600',
      type: 'rightAligned',
      valueFormatter: (params) => Number(params.value || 0).toLocaleString('en-IN'),
    },
    {
      field: 'totalAmountReceived',
      headerName: 'Total Amount',
      width: 160,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => currencyFormatter(params.value),
      cellClass: 'text-sm text-right font-bold text-red-600 bg-red-50',
      type: 'rightAligned',
    },
  ];

  // Grid options
  const gridOptions = {
    animateRows: true,
    suppressMovableColumns: false,
    rowHeight: 40,
    headerHeight: 45,
    pagination: true,
    paginationPageSize: 15,
    paginationPageSizeSelector: [10, 15, 25, 50],
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 80,
    },
    domLayout: 'autoHeight',
  };

  // Line Chart Component
  const LineChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxAmount = Math.max(...data.map(d => d.totalAmountReceived));
    const chartHeight = 300;
    const padding = 40;
    const width = 500;
    const pointSpacing = (width - padding * 2) / (data.length - 1 || 1);

    const points = data.map((item, index) => {
      const x = padding + index * pointSpacing;
      const y = chartHeight + padding - (item.totalAmountReceived / maxAmount) * chartHeight;
      return { x, y, ...item };
    });

    // Create smooth curve path using quadratic Bézier curves
    const pathD = points.map((p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = points[i - 1];
      const cpx = (prev.x + p.x) / 2;
      const cpy = (prev.y + p.y) / 2;
      return `Q ${cpx} ${cpy} ${p.x} ${p.y}`;
    }).join(' ');

    return (
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <svg width="100%" height={chartHeight + 80} viewBox={`0 0 ${width} ${chartHeight + 80}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <style>{`
              @keyframes drawLine {
                from {
                  stroke-dashoffset: 1000;
                }
                to {
                  stroke-dashoffset: 0;
                }
              }
              
              @keyframes fadeInPoint {
                from {
                  opacity: 0;
                  r: 0;
                }
                to {
                  opacity: 1;
                  r: 4;
                }
              }
              
              @keyframes fadeInLabel {
                from {
                  opacity: 0;
                }
                to {
                  opacity: 1;
                }
              }
              
              .chart-line {
                stroke-dasharray: 1000;
                animation: drawLine 2s ease-out forwards;
              }
              
              .chart-point {
                animation: fadeInPoint 0.6s ease-out forwards;
              }
              
              .chart-label {
                animation: fadeInLabel 0.8s ease-out forwards;
              }
            `}</style>
          </defs>
          {/* Y-axis */}
          <line x1={padding} y1={padding} x2={padding} y2={chartHeight + padding} stroke="#ccc" strokeWidth="1" />
          
          {/* X-axis */}
          <line x1={padding} y1={chartHeight + padding} x2={width - padding} y2={chartHeight + padding} stroke="#ccc" strokeWidth="1" />

          {/* Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
            const value = Math.round((maxAmount * ratio) / 100000) * 100000;
            const y = chartHeight + padding - (chartHeight * ratio);
            return (
              <g key={`y-label-${i}`}>
                <text x={padding - 10} y={y + 5} textAnchor="end" fontSize="12" fill="#666">
                  ₹{(value / 100000).toFixed(1)}L
                </text>
                <line x1={padding - 3} y1={y} x2={padding} y2={y} stroke="#ccc" strokeWidth="1" />
              </g>
            );
          })}

          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
            const y = chartHeight + padding - (chartHeight * ratio);
            return (
              <line 
                key={`grid-${i}`} 
                x1={padding} 
                y1={y} 
                x2={width - padding} 
                y2={y} 
                stroke="#f0f0f0" 
                strokeWidth="1"
              />
            );
          })}

          {/* Line path with smooth curves */}
          <path d={pathD} className="chart-line" stroke="#3b82f6" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />

          {/* Points and labels */}
          {points.map((p, index) => {
            const dateObj = new Date(p.date);
            const dateStr = `${dateObj.getDate()}-${dateObj.toLocaleString('en-US', { month: 'short' })}`;
            const delayFactor = index * 0.08;

            return (
              <g key={`point-${index}`}>
                {/* Circle point with animation */}
                <circle 
                  cx={p.x} 
                  cy={p.y} 
                  className="chart-point" 
                  fill="#3b82f6"
                  style={{ animationDelay: `${1.5 + delayFactor}s` }}
                />
                
                {/* Value label */}
                <text 
                  x={p.x} 
                  y={p.y - 15} 
                  textAnchor="middle" 
                  fontSize="11" 
                  fill="#333"
                  fontWeight="bold"
                  className="chart-label"
                  style={{ animationDelay: `${1.8 + delayFactor}s` }}
                >
                  ₹{(p.totalAmountReceived / 100000).toFixed(1)}L
                </text>

                {/* Date label */}
                <text 
                  x={p.x} 
                  y={chartHeight + padding + 20} 
                  textAnchor="middle" 
                  fontSize="11" 
                  fill="#666"
                  className="chart-label"
                  style={{ animationDelay: `${1.8 + delayFactor}s` }}
                  transform={`rotate(45 ${p.x} ${chartHeight + padding + 20})`}
                >
                  {dateStr}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  return (
    <section>
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Date-Wise Payment Report</h2>
            <p className="text-sm text-gray-500 mt-1">Daily payment and advance trends analysis</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('sheet')}
                disabled={isLoading}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                  viewMode === 'sheet'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="fas fa-table mr-2"></i>Sheet
              </button>
              <button
                onClick={() => setViewMode('chart')}
                disabled={isLoading}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                  viewMode === 'chart'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="fas fa-chart-bar mr-2"></i>Chart
              </button>
              <button
                onClick={() => setViewMode('both')}
                disabled={isLoading}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                  viewMode === 'both'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="fas fa-th mr-2"></i>Both
              </button>
            </div>

            <button
              onClick={fetchDateWisePaymentReport}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'animate-spin' : ''}`}></i>
              <span>{isLoading ? 'Loading...' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-semibold">
              <i className="fas fa-exclamation-circle mr-2"></i>
              {error}
            </p>
          </div>
        )}

        {/* Summary Statistics */}
        {reportData.length > 0 && summaryStats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-semibold uppercase">Total Days</p>
              <p className="text-lg font-bold text-blue-900">
                {summaryStats.totalDays}
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-3 rounded-lg border border-red-200">
              <p className="text-xs text-red-700 font-semibold uppercase">Total Amount</p>
              <p className="text-lg font-bold text-red-900">
                ₹{(summaryStats.totalAmount / 100000).toFixed(1)}L
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border border-green-200">
              <p className="text-xs text-green-700 font-semibold uppercase">Total Advances</p>
              <p className="text-lg font-bold text-green-900">
                {summaryStats.totalAdvances.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-700 font-semibold uppercase">Avg per Day</p>
              <p className="text-lg font-bold text-purple-900">
                ₹{(summaryStats.averagePerDay / 100000).toFixed(2)}L
              </p>
            </div>
          </div>
        )}

        {/* Data Sheet & Chart Container */}
        <div className={`flex flex-col ${viewMode === 'both' ? 'lg:flex-row gap-4' : ''}`}>
          {/* Data Sheet View */}
          {(viewMode === 'sheet' || viewMode === 'both') && (
            <div className={viewMode === 'both' ? 'lg:w-1/2' : 'w-full'}>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Data Sheet</h3>
              <div className="ag-theme-alpine rounded-lg overflow-hidden border border-gray-200" style={{ height: 'auto' }}>
                {isLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                      <i className="fas fa-spinner fa-spin text-4xl text-gray-400 mb-4"></i>
                      <p className="text-gray-500 font-semibold">Loading report...</p>
                    </div>
                  </div>
                ) : reportData.length === 0 ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                      <i className="fas fa-inbox text-4xl text-gray-300 mb-4"></i>
                      <p className="text-gray-500 font-semibold">No payment records found</p>
                    </div>
                  </div>
                ) : (
                  <AgGridReact
                    columnDefs={columnDefs}
                    rowData={reportData}
                    gridOptions={gridOptions}
                    theme="legacy"
                    onGridReady={(params) => setGridApi(params.api)}
                    className="w-full"
                  />
                )}
              </div>
            </div>
          )}

          {/* Line Chart View */}
          {(viewMode === 'chart' || viewMode === 'both') && reportData.length > 0 && (
            <div className={viewMode === 'both' ? 'lg:w-1/2' : 'w-full'}>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Amount Trend</h3>
              <LineChart data={reportData} />
            </div>
          )}
        </div>

        {/* Footer Info */}
        {reportData.length > 0 && (
          <div className="mt-6 text-right text-xs text-gray-500 pt-4 border-t">
            Showing {reportData.length} date record{reportData.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </section>
  );
};

export default DateWisePaymentReport;
